import React, { useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

/**
 * Renders the row "..." actions menu in a portal with fixed positioning so it
 * is never clipped by the horizontally-scrolling table wrapper
 * (.appt-table-scroll). Anchored to the clicked dots icon; follows it on
 * scroll/resize and flips above the icon when there is no room below.
 */
export default function FloatingActionMenu({ anchorEl, className = "", children }) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!anchorEl) return undefined;

    const update = () => {
      const rect = anchorEl.getBoundingClientRect();
      const menuHeight = menuRef.current ? menuRef.current.offsetHeight : 0;
      const gap = 4;
      let top = rect.bottom + gap;
      if (menuHeight && top + menuHeight > window.innerHeight - 8 && rect.top - menuHeight - gap > 8) {
        top = rect.top - menuHeight - gap;
      }
      const right = Math.max(8, window.innerWidth - rect.right);
      setPos((prev) =>
        prev && prev.top === top && prev.right === right ? prev : { top, right }
      );
    };

    update();
    // second pass once the menu has rendered and its height is known
    const raf = window.requestAnimationFrame(update);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchorEl]);

  if (!anchorEl) return null;

  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      className={className}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        top: pos ? pos.top : -9999,
        right: pos ? pos.right : 0,
        left: "auto",
        zIndex: 1050,
        minWidth: 140,
        visibility: pos ? "visible" : "hidden",
      }}
    >
      {children}
    </div>,
    document.body
  );
}
